import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../../utils/googleMaps";

/**
 * Google Places Autocomplete for a venue/address field.
 *
 * - Initializes the Autocomplete widget ONCE on mount. Callbacks are stored
 *   in refs so we don't tear down + rebuild on every parent render (which
 *   caused duplicate dropdowns).
 * - Falls back to a plain input if Google Maps can't load.
 */
export default function VenueAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Name of venue or full address",
  className = "",
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onSelectRef = useRef(onSelect);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  // Keep the latest callbacks without retriggering the effect.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !inputRef.current) return;
        if (autocompleteRef.current) return; // already attached

        try {
          const ac = new google.maps.places.Autocomplete(inputRef.current, {
            fields: [
              "formatted_address",
              "name",
              "address_components",
              "geometry",
              "place_id",
            ],
            componentRestrictions: { country: "us" },
            types: ["geocode", "establishment"],
          });
          ac.addListener("place_changed", () => {
            const place = ac.getPlace();
            if (!place) return;
            const comps = place.address_components || [];
            const zip = comps.find((c) => c.types.includes("postal_code"))?.short_name || "";
            const state = comps.find((c) => c.types.includes("administrative_area_level_1"))?.short_name || "";
            const country = comps.find((c) => c.types.includes("country"))?.short_name || "";
            const city =
              comps.find((c) => c.types.includes("locality"))?.long_name ||
              comps.find((c) => c.types.includes("postal_town"))?.long_name ||
              comps.find((c) => c.types.includes("sublocality"))?.long_name ||
              "";
            const address = place.formatted_address || "";
            const name = place.name && place.name !== address ? place.name : "";
            const combined = name ? `${name}, ${address}` : address;
            if (combined) onChangeRef.current?.(combined);
            onSelectRef.current?.({
              name,
              address,
              combined,
              zip,
              state,
              country,
              city,
              lat: place.geometry?.location?.lat?.() ?? null,
              lng: place.geometry?.location?.lng?.() ?? null,
              placeId: place.place_id || "",
            });
          });
          autocompleteRef.current = ac;
          setReady(true);
        } catch (e) {
          console.error("Places Autocomplete init failed:", e);
          setFailed(true);
        }
      })
      .catch((e) => {
        console.warn("Google Maps failed to load, using plain input:", e);
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      // Tear down the Autocomplete + its dropdown when the component unmounts
      if (autocompleteRef.current && window.google?.maps?.event) {
        try {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        } catch {
          /* ignore */
        }
        autocompleteRef.current = null;
      }
      // Remove any orphaned .pac-container elements Google appended to <body>
      if (typeof document !== "undefined") {
        document.querySelectorAll(".pac-container").forEach((el) => el.remove());
      }
    };
  }, []);

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={failed ? "Street, city, state, ZIP" : placeholder}
        className={`bb-input ${className}`}
        autoComplete="off"
      />
      {!ready && !failed && (
        <p className="text-xs text-birdie-charcoal/50 mt-1">Loading place suggestions…</p>
      )}
      {failed && (
        <p className="text-xs text-birdie-charcoal/50 mt-1">
          Suggestions unavailable right now — type the full address.
        </p>
      )}
    </div>
  );
}
